/**
 * Auth Store - Zustand store for authentication state management.
 *
 * Manages user authentication state, tokens (via TokenManager), and
 * the 60-minute inactivity timeout that requires re-authentication.
 *
 * @see Requirements 7.5, 10.7
 */

import { create } from 'zustand';
import { TokenManager } from '@/services/auth';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Inactivity timeout: 60 minutes in milliseconds. */
const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AuthState {
  /** Whether the user has a valid authenticated session. */
  isAuthenticated: boolean;
  /** Cognito user sub ID. */
  userId: string | null;
  /** User's email address. */
  email: string | null;
  /** Timestamp (ms) of the last user interaction for inactivity tracking. */
  lastActivity: number;
  /** Whether an auth operation is in progress. */
  isLoading: boolean;
  /** Last error message from an auth operation. */
  error: string | null;
}

export interface AuthActions {
  /** Login with email and password. */
  login: (email: string, password: string) => Promise<void>;
  /** Logout and clear all tokens. */
  logout: () => Promise<void>;
  /** Register a new account with email and password. */
  register: (email: string, password: string) => Promise<void>;
  /** Update the last activity timestamp (call on user interactions). */
  updateLastActivity: () => void;
  /** Check if the inactivity timeout has been exceeded; if so, clear session. */
  checkInactivity: () => boolean;
  /** Initialize auth state (restore session on app startup). */
  initialize: () => Promise<void>;
  /** Clear any error state. */
  clearError: () => void;
}

export type AuthStore = AuthState & AuthActions;

// ─── Store ───────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthStore>((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  isAuthenticated: false,
  userId: null,
  email: null,
  lastActivity: Date.now(),
  isLoading: false,
  error: null,

  // ── Actions ────────────────────────────────────────────────────────────────
  login: async (email, password) => {
    console.log('[AUTH] authStore.login() called with email:', email);
    set({ isLoading: true, error: null });
    try {
      await TokenManager.login(email, password);
      const state = TokenManager.getState();
      console.log('[AUTH] authStore.login() TokenManager state after login:', JSON.stringify({ isAuthenticated: state.isAuthenticated, hasTokens: !!state.tokens }));

      // Extract userId from ID token (sub claim)
      let userId: string | null = null;
      if (state.tokens?.idToken) {
        userId = extractUserIdFromToken(state.tokens.idToken);
      }

      console.log('[AUTH] authStore.login() setting isAuthenticated: true, userId:', userId);
      set({
        isAuthenticated: true,
        userId,
        email,
        lastActivity: Date.now(),
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('[AUTH] authStore.login() error:', error);
      // Generic error message per Requirement 7.6
      set({
        isAuthenticated: false,
        isLoading: false,
        error: 'Invalid credentials',
      });
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await TokenManager.logout();
    } catch {
      // Best-effort; clear local state regardless
    }
    set({
      isAuthenticated: false,
      userId: null,
      email: null,
      lastActivity: 0,
      isLoading: false,
      error: null,
    });
  },

  register: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      await TokenManager.register(email, password);
      set({ isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      set({ isLoading: false, error: message });
    }
  },

  updateLastActivity: () => {
    set({ lastActivity: Date.now() });
  },

  checkInactivity: () => {
    const { lastActivity, isAuthenticated } = get();
    if (!isAuthenticated) return false;

    const elapsed = Date.now() - lastActivity;
    if (elapsed > INACTIVITY_TIMEOUT_MS) {
      // Session expired due to inactivity — clear tokens
      TokenManager.clearSession();
      set({
        isAuthenticated: false,
        userId: null,
        email: null,
        error: 'SESSION_EXPIRED_INACTIVITY',
      });
      return true;
    }
    return false;
  },

  initialize: async () => {
    console.log('[AUTH] authStore.initialize() called');
    set({ isLoading: true });
    try {
      await TokenManager.configureAuth();
      const state = TokenManager.getState();
      console.log('[AUTH] authStore.initialize() TokenManager state:', JSON.stringify(state));

      if (state.isAuthenticated && state.tokens) {
        const userId = extractUserIdFromToken(state.tokens.idToken);
        console.log('[AUTH] authStore.initialize() -> authenticated, userId:', userId);
        set({
          isAuthenticated: true,
          userId,
          lastActivity: Date.now(),
          isLoading: false,
          error: null,
        });
      } else {
        console.log('[AUTH] authStore.initialize() -> NOT authenticated');
        set({ isAuthenticated: false, isLoading: false });
      }
    } catch (err) {
      console.error('[AUTH] authStore.initialize() error:', err);
      set({ isAuthenticated: false, isLoading: false });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Extract the user ID (sub claim) from a Cognito ID token JWT.
 */
function extractUserIdFromToken(idToken: string): string | null {
  try {
    const payload = idToken.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload)) as { sub?: string };
    return decoded.sub ?? null;
  } catch {
    return null;
  }
}
